import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Table, { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import FileOutlined from '@ant-design/icons/FileOutlined';
import { PjmBatteryInfo } from 'src/domain/pjm/dto/PjmBatteryInfo';
import { LocationBatteryModal } from './LocationBatteryModal';
import _ from 'lodash';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import Flex from 'antd/lib/flex';
import { ActionList } from 'src/components/table/actionList/actionList';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import modal from 'antd/lib/modal';
import * as uuid from 'uuid';

interface LocationBatteryInfoTableProps {
    value: Array<PjmBatteryInfo & { _id: string }>;
    onHide: () => void;
    onChange: (value: Array<PjmBatteryInfo & { _id: string }>) => void;
}

export function LocationBatteryInfoTable({ value, onHide, onChange }: LocationBatteryInfoTableProps) {
    let records = value;
    const [showModal, setShowModal] = useState<(PjmBatteryInfo & { _id: string }) | null>(null);

    function onBatteryInfoChange(data: PjmBatteryInfo & { _id: string }) {
        const isNew = records.filter(it => it._id === data._id).length === 0;
        if (isNew) {
            records.push(data);
        } else {
            for (let i = 0; i < records.length; i++) {
                if (records[i]._id === data._id) {
                    records[i] = { ...data };
                }
            }
        }

        onChange(records);
        setShowModal(null);
    }

    function showAddBatteryModal() {
        setShowModal({
            _id: uuid.v4(),
        });
    }

    function onModalClose(actionTaken: boolean) {
        setShowModal(null);
    }

    function deleteWithConfirmation(index: number) {
        modal.confirm({
            icon: <ExclamationCircleOutlined />,
            content: `Deleting battery details. Are you sure?`,
            onOk() {
                // we expect to have zero or one batter - so if we delete one - we will have zero
                onChange([]);
            },
            onCancel() {},
        });
    }

    const actions = [
        {
            title: 'Edit',
            onClick:
                ([record, index]: [record: PjmBatteryInfo & { _id: string }, index: number]) =>
                () =>
                    setShowModal(record),
        },
        {
            title: 'Delete',
            onClick:
                ([record, index]: [record: PjmBatteryInfo & { _id: string }, index: number]) =>
                () =>
                    deleteWithConfirmation(index),
        },
    ];

    const columns: ColumnsType<PjmBatteryInfo & { _id: string }> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            fixed: 'left',
        },
        {
            title: 'Max Output (kW)',
            dataIndex: 'maxOutput',
            key: 'maxOutput',
        },
        {
            title: 'Battery Capacity (kW 1C)',
            dataIndex: 'capacity',
            key: 'capacity',
        },
        {
            title: 'Vintage',
            dataIndex: 'vintage',
            key: 'vintage',
        },
        {
            title: 'Chemistry',
            dataIndex: 'chemistry',
            key: 'chemistry',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: 'Note',
            dataIndex: 'note',
            key: 'note',
            render: (text, record) => (_.isEmpty(text) ? 'No' : 'Yes'),
        },
        {
            title: 'Agreement Type',
            dataIndex: 'agreementType',
            key: 'agreementType',
        },
        {
            title: 'Injection Rights (MW)',
            dataIndex: 'injectionRight',
            key: 'injectionRight',
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            minWidth: 60,
            render: (text, record, index) => {
                return <ActionList actions={actions} item={[record, index]} />;
            },
        },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Flex style={{ width: '100%' }} justify={'space-between'} align={'center'}>
                {records.length === 0 ? ( // expected only one battery for a location
                    <Button onClick={showAddBatteryModal} icon={<FileOutlined />}>
                        Add Battery
                    </Button>
                ) : (
                    <div>&nbsp;</div>
                )}
                <Button onClick={() => onHide()} icon={<CloseOutlined />} type="text">
                    Hide
                </Button>
            </Flex>

            <Table dataSource={value} columns={columns} pagination={false} rowKey="_id" tableLayout={'auto'} scroll={{ x: 'scroll' }} />

            {!!showModal && (
                <LocationBatteryModal key="pjm-location-battery-modal" onClose={onModalClose} onChange={onBatteryInfoChange} value={showModal} />
            )}
        </Space>
    );
}
